import { LegalFooter } from "./legal.js";
import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from "react";
import { LoadingState } from "@corefinops/ui";
import { useMarkNotificationRead, useNotifications } from "./api/hooks.js";
import { useSession } from "./api/session.js";
import { ScreenErrorBoundary } from "./ErrorBoundary.js";
import { matchRoute, navigate, useRoutePath } from "./router.js";
import { OverviewScreen } from "./screens/OverviewScreen.js";

/** Route-level code splitting (B7): each screen is its own chunk, fetched on first visit. */
const PortfolioScreen = lazy(() => import("./screens/PortfolioScreen.js").then((m) => ({ default: m.PortfolioScreen })));
const EconomicsScreen = lazy(() => import("./screens/EconomicsScreen.js").then((m) => ({ default: m.EconomicsScreen })));
const WavePlannerScreen = lazy(() => import("./screens/WavePlannerScreen.js").then((m) => ({ default: m.WavePlannerScreen })));
const DualRunScreen = lazy(() => import("./screens/DualRunScreen.js").then((m) => ({ default: m.DualRunScreen })));
const DecommissionScreen = lazy(() => import("./screens/DecommissionScreen.js").then((m) => ({ default: m.DecommissionScreen })));
const IntelligenceScreen = lazy(() => import("./screens/IntelligenceScreen.js").then((m) => ({ default: m.IntelligenceScreen })));
const OptimizeScreen = lazy(() => import("./screens/OptimizeScreen.js").then((m) => ({ default: m.OptimizeScreen })));
const GovernanceScreen = lazy(() => import("./screens/GovernanceScreen.js").then((m) => ({ default: m.GovernanceScreen })));
const ValueLedgerScreen = lazy(() => import("./screens/ValueLedgerScreen.js").then((m) => ({ default: m.ValueLedgerScreen })));
const ArchitectureScreen = lazy(() => import("./screens/ArchitectureScreen.js").then((m) => ({ default: m.ArchitectureScreen })));
const ExplorerScreen = lazy(() => import("./screens/ExplorerScreen.js").then((m) => ({ default: m.ExplorerScreen })));
const ReportsScreen = lazy(() => import("./screens/ReportsScreen.js").then((m) => ({ default: m.ReportsScreen })));
const SettingsScreen = lazy(() => import("./screens/SettingsScreen.js").then((m) => ({ default: m.SettingsScreen })));
const ActionCenterScreen = lazy(() => import("./screens/ActionCenterScreen.js").then((m) => ({ default: m.ActionCenterScreen })));
const LegacyImportScreen = lazy(() => import("./screens/LegacyImportScreen.js").then((m) => ({ default: m.LegacyImportScreen })));
const SavingsRadarScreen = lazy(() => import("./screens/SavingsRadarScreen.js").then((m) => ({ default: m.SavingsRadarScreen })));
const BillingScreen = lazy(() => import("./screens/BillingScreen.js").then((m) => ({ default: m.BillingScreen })));
const ExpertHelpScreen = lazy(() => import("./screens/ExpertHelpScreen.js").then((m) => ({ default: m.ExpertHelpScreen })));

/**
 * CoreFinOps application shell (Req 26, UX-01). Five primary sections group
 * every screen by the job to be done; every existing route is preserved, so
 * deep links and bookmarks keep working. The browser talks only to the API via
 * the session's ApiClient.
 */

interface NavItem {
  readonly label: string;
  readonly path: string;
}

export const SECTIONS: ReadonlyArray<{ label: string; items: readonly NavItem[] }> = [
  { label: "Home", items: [{ label: "Command Center", path: "/overview" }, { label: "Explorer", path: "/explorer" }] },
  { label: "Optimize", items: [{ label: "Savings Radar", path: "/radar" }, { label: "Optimize", path: "/optimize" }, { label: "Intelligence", path: "/intelligence" }] },
  { label: "Transform", items: [{ label: "Migration", path: "/migration" }, { label: "Architecture", path: "/architecture" }, { label: "Operations", path: "/operations" }] },
  { label: "Actions", items: [{ label: "Action Center", path: "/actions" }, { label: "Approvals", path: "/governance" }] },
  { label: "Outcomes", items: [{ label: "Value Ledger", path: "/value" }, { label: "Reports", path: "/reports" }] },
];

/** Account and set-up links, kept out of the five job-to-be-done sections. */
export const UTILITY: readonly NavItem[] = [
  { label: "Settings", path: "/settings" },
  { label: "Plan & billing", path: "/settings/billing" },
  { label: "Expert help", path: "/help" },
  { label: "Inventory", path: "/inventory" },
  { label: "Governance policies", path: "/governance/policies" },
];

const ALL_ITEMS = [...SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, section: s.label }))), ...UTILITY.map((i) => ({ ...i, section: "Account" }))];

/** The nav item owning a route: the longest path that prefixes it. */
export function activeItemFor(path: string): (NavItem & { section: string }) | undefined {
  return ALL_ITEMS.filter((i) => path === i.path || path.startsWith(`${i.path}/`)).sort((a, b) => b.path.length - a.path.length)[0];
}

const ROUTES = [
  "/overview",
  "/radar",
  "/value",
  "/migration",
  "/migration/waves",
  "/migration/import",
  "/migration/workloads/:id",
  "/intelligence",
  "/optimize",
  "/optimize/actions",
  "/actions",
  "/operations",
  "/operations/dual-run",
  "/operations/decommission",
  "/operations/decommission/:id",
  "/architecture",
  "/explorer",
  "/governance",
  "/governance/policies",
  "/inventory",
  "/reports",
  "/settings",
  "/settings/billing",
  "/help",
];

export function App(): ReactNode {
  const { session, signOut, switchTenant } = useSession();
  const raw = useRoutePath();
  const route = matchRoute(ROUTES, raw) ?? { path: "/overview", params: {} };
  const active = activeItemFor(route.path.replace(/\/:id$/, ""));

  return (
    <div className="cfo-app">
      <aside className="cfo-sidebar">
        <div className="cfo-brandrow">
          <img className="cfo-brand-logo" src="/apple-touch-icon.png" alt="" aria-hidden="true" />
          <span className="cfo-brand-name">CoreFinOps</span>
        </div>
        <nav className="cfo-nav" aria-label="Primary">
          {SECTIONS.map((s) => (
            <div key={s.label} role="group" aria-label={s.label} className="cfo-navsection">
              <div className="cfo-navsection-label" aria-hidden="true">{s.label}</div>
              {s.items.map((n) => {
                const isActive = active?.path === n.path;
                return (
                  <button
                    key={n.path}
                    type="button"
                    className={`cfo-navitem${isActive ? " active" : ""}`}
                    onClick={() => navigate(n.path)}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {NAV_ICON[n.path] ?? null}
                    <span>{n.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
          <div role="group" aria-label="Account" className="cfo-navsection cfo-navutility">
            <div className="cfo-navsection-label" aria-hidden="true">Account</div>
            {UTILITY.map((n) => {
              const isActive = active?.path === n.path;
              return (
                <button key={n.path} type="button" className={`cfo-navitem${isActive ? " active" : ""}`} onClick={() => navigate(n.path)} aria-current={isActive ? "page" : undefined}>
                  {NAV_ICON[n.path] ?? null}
                  <span>{n.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </aside>
      <div className="cfo-main">
        <header className="cfo-topbar">
          <span className="cfo-section">
            {active ? <><span className="cfo-muted">{active.section} ›</span> {active.label}</> : "Command Center"}
          </span>
          <span className="cfo-userchip">
            <NotificationsBell />
            <span className="cfo-ws" data-testid="signed-in-as" title={`${session.email ?? session.userSub} · ${session.tenantName ?? session.tenantId}`}>
              {session.email ?? session.userSub} · <strong>{session.tenantName ?? `tenant ${session.tenantId.slice(0, 8)}…`}</strong>
              {session.roles?.length ? ` (${session.roles.join(", ")})` : ""}
            </span>
            {switchTenant ? <button type="button" onClick={switchTenant} className="cfo-btn-ghost">Switch workspace</button> : null}
            {signOut ? <button type="button" onClick={signOut} className="cfo-btn-ghost">Sign out</button> : null}
          </span>
        </header>
        <main className="cfo-content">
          <ScreenErrorBoundary resetKey={raw}>
            <Suspense fallback={<LoadingState label="Loading screen" />}>{renderScreen(route)}</Suspense>
          </ScreenErrorBoundary>
        </main>
        <footer className="cfo-content"><LegalFooter /></footer>
      </div>
    </div>
  );
}

/** In-app alerts (RET-01): unread count + the latest alerts, mark read on open. */
function NotificationsBell(): ReactNode {
  const q = useNotifications();
  const markRead = useMarkNotificationRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const unread = q.data?.unread ?? 0;
  return (
    <div className="cfo-bell" ref={ref}>
      <button type="button" className="cfo-btn-ghost" aria-expanded={open} aria-label={`Alerts${unread ? `, ${unread} unread` : ""}`} onClick={() => setOpen((o) => !o)}>
        {svg("M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9|M10.3 21a1.94 1.94 0 0 0 3.4 0")}
        {unread ? <span className="cfo-bell-count">{unread > 99 ? "99+" : unread}</span> : null}
      </button>
      {open ? (
        <div className="cfo-bell-panel" role="dialog" aria-label="Alerts">
          <div className="cfo-card-row">
            <strong>Alerts</strong>
            {unread ? <button type="button" className="cfo-pill" onClick={() => markRead.mutate("all")}>Mark all read</button> : null}
          </div>
          {!q.data?.notifications.length ? (
            <p className="cfo-muted">Nothing yet.</p>
          ) : (
            <ul className="cfo-alert-list">
              {q.data.notifications.slice(0, 12).map((n) => (
                <li key={n.notificationId} className={n.readAt ? undefined : "unread"}>
                  <button
                    type="button"
                    className="cfo-linklike"
                    onClick={() => {
                      if (!n.readAt) markRead.mutate(n.notificationId);
                      setOpen(false);
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <strong>{n.title}</strong>
                    {n.body ? <span className="cfo-muted"> — {n.body}</span> : null}
                  </button>
                  <span className="cfo-muted">{n.createdAt.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Compact line icons per nav item (inline so there's no icon dependency). */
const svg = (d: string): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.split("|").map((p, i) => (
      <path key={i} d={p} />
    ))}
  </svg>
);

const NAV_ICON: Record<string, ReactNode> = {
  "/overview": svg("M3 9.5 12 3l9 6.5|M5 9v11h14V9"),
  "/radar": svg("M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0|M12 12m-5 0a5 5 0 1 0 10 0a5 5 0 1 0-10 0|M12 12l6-6"),
  "/value": svg("M3 17l6-6 4 4 7-8|M14 7h5v5"),
  "/migration": svg("M4 12h13|M13 6l6 6-6 6"),
  "/intelligence": svg("M9 18h6|M10 21h4|M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.2 1 2.5h6c0-1.3.3-1.8 1-2.5A6 6 0 0 0 12 3z"),
  "/optimize": svg("M4 6h10|M18 6h2|M4 12h4|M12 12h8|M4 18h12|M18 18h2|M14 4v4|M8 10v4|M16 16v4"),
  "/actions": svg("M9 12l2 2 4-4|M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"),
  "/operations": svg("M3 12h4l2 6 4-14 2 8h6"),
  "/architecture": svg("M4 4h6v6H4z|M14 14h6v6h-6z|M7 10v4h7"),
  "/explorer": svg("M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z|M20 20l-3.5-3.5"),
  "/governance": svg("M12 3l8 3v5c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z"),
  "/reports": svg("M7 3h7l5 5v13H7z|M14 3v5h5|M9 13h6|M9 17h6"),
  "/settings": svg("M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z|M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2l-.4-2.6H9.9l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2l.4 2.6h4.2l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z"),
  "/settings/billing": svg("M3 7h18v10H3z|M3 11h18|M7 15h3"),
  "/help": svg("M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z|M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3|M12 17h.01"),
};

function renderScreen(route: { path: string; params: Record<string, string> }): ReactNode {
  switch (route.path) {
    case "/overview":
      return <OverviewScreen />;
    case "/radar":
      return <SavingsRadarScreen />;
    case "/value":
      return <ValueLedgerScreen />;
    case "/migration":
      return <PortfolioScreen />;
    case "/migration/waves":
      return <WavePlannerScreen />;
    case "/migration/import":
      return <LegacyImportScreen />;
    case "/actions":
    case "/optimize/actions":
      return <ActionCenterScreen />;
    case "/migration/workloads/:id":
      return <EconomicsScreen workloadId={route.params["id"]!} />;
    case "/intelligence":
      return <IntelligenceScreen />;
    case "/optimize":
      return <OptimizeScreen />;
    case "/operations":
    case "/operations/dual-run":
      return <DualRunScreen />;
    case "/operations/decommission":
    case "/operations/decommission/:id":
      return <DecommissionScreen />;
    case "/architecture":
      return <ArchitectureScreen />;
    case "/explorer":
      return <ExplorerScreen />;
    case "/governance":
      return <GovernanceScreen />;
    case "/governance/policies":
      return <GovernanceScreen initialView="policies" />;
    case "/reports":
      return <ReportsScreen />;
    case "/settings":
      return <SettingsScreen />;
    case "/inventory":
      return <SettingsScreen initialTab="sync" />;
    case "/settings/billing":
      return <BillingScreen />;
    case "/help":
      return <ExpertHelpScreen />;
    default:
      return <OverviewScreen />;
  }
}
